import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isInApp } from '../../utils/isInApp';
import AppPaymentBlocker from '../../components/AppPaymentBlocker';
import { calculateGroupPrice, getGroupPricingRows, GROUP_PLAN_PRICES, getGroupPromoSlug } from '../../utils/groupPricing';
import {
  GROUP50_EVENT_END_LABEL,
  GROUP50_MIN_QUANTITY,
  isGroup50EventActive,
  isGroup50PromoCode,
} from '../../utils/group50Campaign';
import {
  PRO50_EVENT_END_LABEL,
  PRO50_PLAN_ORDER,
  PRO50_PLAN_LABELS,
  getPro50DiscountPercent,
  isPro50Active,
} from '../../utils/pro50Campaign';
import {
  trackGroup50CheckoutView,
  trackGroup50Event,
  trackPro50CheckoutView,
  trackPro50Event,
  trackSchoolPurchaseEvent,
} from '../../utils/eventTracker';
import {
  extractUtmFromUrl,
  getFirstUtmFromStorage,
  getMetaMatchParams,
  getUtmFromStorage,
} from '../../utils/analytics';
import { downloadBase64Pdf, downloadDocumentPackage } from '../../utils/quotationDocs';
import BulkPaymentLinkCheckout from './BulkPaymentLinkCheckout';
import './BulkPurchasePage.css';

const BULK_PURCHASE_DRAFT_KEY = 'whatquiz_bulk_purchase_draft';
const QUOTE_NUMBER_RE = /^WQ-\d{8}-\d{1,6}$/;
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const S2B_PRODUCT_URL = 'https://s2b.kr/Edutech/product/productDetail.do?estimatecode=202508043796515';

// 학교 구매는 대부분 "서류 → 품의 → 학교장터·법인카드" 순서라 서류 받기가 기본이다.
// 결제 의도가 분명한 진입(intent=pay, 견적서 결제 링크, 이벤트 '구매하기'의 promo)만 카드로 연다.
export const getInitialPurchasePath = (searchParams, quoteNumber = '') => {
  const intent = searchParams.get('intent');
  if (intent === 'quote') return 'docs';
  if (intent === 'pay' || quoteNumber || searchParams.get('promo')) return 'card';
  return 'docs';
};

const formatKstDate = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return `${kst.getUTCMonth() + 1}월 ${kst.getUTCDate()}일`;
};

const _getBulkPlanLookup = (promoCode = '') => {
  return Object.fromEntries(
    Object.entries(GROUP_PLAN_PRICES).map(([planId, plan]) => [
      planId,
      { id: `pro_${planId}`, label: `${plan.label}권`, days: plan.days, price: plan.price, promoPrice: plan.price },
    ])
  );
};

const getApiBaseUrl = () => {
  const host = window.location.hostname;
  if (host.includes('localhost')) return 'http://localhost:8000';
  if (host.includes('whatquiztest')) return 'https://quiz-api-test-397277209875.us-central1.run.app';
  return 'https://quiz-api-397277209875.us-central1.run.app';
};

const vatSplit = (totalIncl) => {
  const supply = Math.round(totalIncl / 1.1);
  return { supply, vat: totalIncl - supply };
};

const loadTossSdk = () => new Promise((resolve, reject) => {
  if (window.TossPayments) return resolve();
  const script = document.createElement('script');
  script.src = 'https://js.tosspayments.com/v1/payment';
  script.async = true;
  script.onload = () => resolve();
  script.onerror = () => reject(new Error('토스 페이먼츠 SDK 로드 실패'));
  document.head.appendChild(script);
});

const BulkPurchasePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // query string 우선, 그 다음 localStorage 초안
  const initialDraft = useMemo(() => {
    const fromQuery = {
      schoolName: searchParams.get('school') || '',
      planId: searchParams.get('plan') || '',
      quantity: parseInt(searchParams.get('qty') || '0', 10) || 0,
      contactName: searchParams.get('name') || '',
      email: searchParams.get('email') || '',
      promoCode: searchParams.get('promo') || '',
    };
    let fromStorage = {};
    try {
      const raw = localStorage.getItem(BULK_PURCHASE_DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.savedAt && (Date.now() - parsed.savedAt) < 60 * 60 * 1000) {
          fromStorage = parsed;
        }
      }
    } catch (_e) { /* ignore */ }
    return {
      schoolName: fromQuery.schoolName || fromStorage.schoolName || '',
      planId: fromQuery.planId || fromStorage.planId || '',
      quantity: fromQuery.quantity || fromStorage.quantity || 0,
      contactName: fromQuery.contactName || fromStorage.contactName || '',
      email: fromQuery.email || fromStorage.email || '',
      promoCode: fromQuery.promoCode || fromStorage.promoCode || '',
    };
  }, [searchParams]);

  const [schoolName, setSchoolName] = useState(initialDraft.schoolName || '');
  const [planKey, setPlanKey] = useState(initialDraft.planId || '12month');
  const [quantity, setQuantity] = useState(initialDraft.quantity || 1);
  const [contactName, setContactName] = useState(initialDraft.contactName || '');
  const [email, setEmail] = useState(initialDraft.email || '');
  const [phone, setPhone] = useState('');
  const promoCode = initialDraft.promoCode || '';
  const promoSlug = useMemo(() => getGroupPromoSlug(promoCode), [promoCode]);
  const activePromoCode = promoSlug || promoCode;
  // 10명 이상 단체 50% 이벤트 (/events/group-50, 9월) — 서버가 같은 규칙으로 금액을 재검증한다
  const isGroup50 = useMemo(() => isGroup50PromoCode(promoCode), [promoCode]);
  // 10월 PRO 최대 50% 이벤트 — 인원 제한 없이 모든 단체 결제에 자동 적용
  const pro50Active = isPro50Active();
  // 견적서 링크의 견적번호 — 10월 이벤트가 견적은 견적일 + 30일 동안 같은 단가로 결제된다
  const quoteNumber = useMemo(() => {
    const raw = String(searchParams.get('quote') || '').trim().toUpperCase();
    return QUOTE_NUMBER_RE.test(raw) ? raw : '';
  }, [searchParams]);
  const [quoteLock, setQuoteLock] = useState(null);
  const [couponPrefix, setCouponPrefix] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [tossReady, setTossReady] = useState(false);

  // 학교 예산(견적서) 흐름 — 같은 입력값·promo로 견적서/행정 서류 ZIP을 만든다
  const [quoteGenerating, setQuoteGenerating] = useState(false);
  const [quoteDocs, setQuoteDocs] = useState([]);
  const [quoteEmailSent, setQuoteEmailSent] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [quoteLockedUntil, setQuoteLockedUntil] = useState('');
  const [officeNoteCopied, setOfficeNoteCopied] = useState(false);
  const schoolInputRef = useRef(null);
  const docsEmailInputRef = useRef(null);
  const docsDoneRef = useRef(null);
  const [purchasePath, setPurchasePath] = useState(() => getInitialPurchasePath(searchParams, quoteNumber));
  const entrySource = searchParams.get('from') || (searchParams.get('promo') ? `promo_${searchParams.get('promo')}` : 'direct');

  // cutover 시간 분기: 페이지 진입/리렌더 시점에 시점별 가격 평가
  const planLookup = useMemo(() => _getBulkPlanLookup(activePromoCode), [activePromoCode]);
  const plan = planLookup[planKey] || planLookup['12month'];

  // groupPricing이 10월 이벤트·견적 보장·전용 특가·인원별 티어를 백엔드와 동일하게 처리한다
  const priceFor = useCallback((key, qty) => (
    calculateGroupPrice(key, qty, activePromoCode, {
      lockedUnitPrice: quoteLock && quoteLock.planId === key ? quoteLock.unitPrice : null,
    })
  ), [activePromoCode, quoteLock]);

  const groupPrice = useMemo(() => priceFor(planKey, quantity), [priceFor, planKey, quantity]);
  const hasPromoPricing = groupPrice.pricingPolicy === 'secret_50';
  const hasGroup50Pricing = groupPrice.pricingPolicy === 'event_group50';
  const hasPro50Pricing = groupPrice.pricingPolicy === 'event_pro50';
  const showPro50Banner = pro50Active || Boolean(quoteLock);
  const discountRows = useMemo(() => getGroupPricingRows().slice().reverse(), []);
  const unitPrice = groupPrice.unitPrice;
  const totalAmount = groupPrice.totalAmount;
  const { supply, vat } = vatSplit(totalAmount);

  useEffect(() => {
    if (!quoteNumber) {
      setQuoteLock(null);
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({ quote: quoteNumber, plan: planKey });
    fetch(`${getApiBaseUrl()}/api/bulk-purchase/quote-lock?${params.toString()}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setQuoteLock(data?.locked
          ? { planId: planKey, unitPrice: data.unit_price, expiresAt: data.expires_at }
          : null);
      })
      .catch(() => { if (!cancelled) setQuoteLock(null); });
    return () => { cancelled = true; };
  }, [quoteNumber, planKey]);

  useEffect(() => {
    loadTossSdk().then(() => setTossReady(true)).catch(() => {
      setErrorMsg('결제 시스템을 불러오지 못했어요. 잠시 후 다시 시도해주세요.');
    });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // GROUP50 결제 페이지 도착 — Meta 최적화 이벤트 (브라우저 픽셀 + 서버 CAPI dedup).
  // promo=group50 진입에서만, 같은 페이지뷰에서 1회만 발화한다 (ref 가드).
  const checkoutViewTrackedRef = useRef(false);
  useEffect(() => {
    if (!isGroup50 || checkoutViewTrackedRef.current) return;
    checkoutViewTrackedRef.current = true;
    trackGroup50CheckoutView({
      plan: planKey,
      quantity,
      value: totalAmount,
    });
  }, [isGroup50]); // eslint-disable-line react-hooks/exhaustive-deps

  // 10월 이벤트가가 적용된 단체 결제 페이지 도착 — Meta 최적화 이벤트(Pro50CheckoutView), 1회
  const pro50CheckoutTrackedRef = useRef(false);
  useEffect(() => {
    if (!hasPro50Pricing || pro50CheckoutTrackedRef.current) return;
    pro50CheckoutTrackedRef.current = true;
    trackPro50CheckoutView({
      plan: planKey,
      quantity,
      value: totalAmount,
      checkoutType: 'bulk',
    });
  }, [hasPro50Pricing]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = useCallback(() => {
    if (!schoolName.trim()) return '학교(기관)명을 입력해주세요.';
    if (!contactName.trim()) return '결제 담당자 이름을 입력해주세요.';
    const emailTrim = email.trim();
    if (!emailTrim || !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(emailTrim)) {
      return '쿠폰을 받을 이메일 주소를 정확히 입력해주세요.';
    }
    const phoneClean = phone.replace(/[^0-9]/g, '');
    if (phoneClean.length < 10 || phoneClean.length > 11) return '휴대폰 번호를 정확히 입력해주세요.';
    if (couponPrefix && !/^[A-Z0-9-]{3,16}$/.test(couponPrefix.toUpperCase())) {
      return '쿠폰 이름은 영문 대문자·숫자·하이픈 3~16자로 입력해주세요.';
    }
    if (quantity < 1 || quantity > 500) return '인원은 1~500명까지 가능해요.';
    if (!agreed) return '결제 안내사항에 동의해주세요.';
    if (!tossReady) return '결제 시스템을 준비하고 있어요. 잠시 후 다시 시도해주세요.';
    return null;
  }, [schoolName, contactName, email, phone, couponPrefix, quantity, agreed, tossReady]);

  const collectQuoteAttribution = () => {
    const urlUtm = extractUtmFromUrl() || {};
    const lastUtm = getUtmFromStorage() || {};
    const firstUtm = getFirstUtmFromStorage() || {};
    const pick = (key) => urlUtm[key] || lastUtm[key] || firstUtm[key] || null;
    return {
      utm_source: pick('utm_source'),
      utm_medium: pick('utm_medium'),
      utm_campaign: pick('utm_campaign'),
      utm_content: pick('utm_content'),
      landing_page: lastUtm.landing_page || firstUtm.landing_page || window.location.pathname || null,
      ...getMetaMatchParams(),
      event_source_url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
    };
  };

  const createQuoteEventId = () => {
    try {
      if (window.crypto?.randomUUID) return `quote-${window.crypto.randomUUID()}`;
    } catch { /* crypto 미지원 */ }
    return `quote-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const collectUtm = () => {
    let utm = {};
    try {
      const stored = localStorage.getItem('whatquiz_utm');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (!parsed.expiresAt || Date.now() <= parsed.expiresAt) {
          utm = {
            utm_source: parsed.utm_source || null,
            utm_medium: parsed.utm_medium || null,
            utm_campaign: parsed.utm_campaign || null,
            utm_content: parsed.utm_content || null,
            landing_page: parsed.landing_page || null,
          };
        }
      }
    } catch (_e) { /* ignore */ }
    return utm;
  };

  const handleSubmit = async () => {
    setErrorMsg('');
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }

    setIsProcessing(true);
    const checkoutMeta = { entry: entrySource, path: 'card', plan: planKey, quantity, promo: activePromoCode };
    trackSchoolPurchaseEvent('card_checkout_started', checkoutMeta);
    let failReason = 'exception';
    try {
      const apiBase = getApiBaseUrl();
      const utm = collectUtm();

      const orderRes = await fetch(`${apiBase}/api/bulk-purchase/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_name: schoolName.trim(),
          plan_id: planKey,
          quantity,
          apply_promo: true,
          contact_name: contactName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.replace(/[^0-9]/g, ''),
          coupon_prefix: couponPrefix ? couponPrefix.toUpperCase() : null,
          promo_code: activePromoCode || null,
          quote_number: quoteNumber || null,
          ...utm,
          referrer: document.referrer || null,
        }),
      });

      if (!orderRes.ok) {
        failReason = `http_${orderRes.status}`;
        const errData = await orderRes.json().catch(() => ({}));
        throw new Error(errData.detail || '주문을 만들지 못했어요.');
      }

      const orderData = await orderRes.json();
      const { order_id, amount, order_name, toss_config } = orderData;

      failReason = 'toss';
      const tossPayments = window.TossPayments(toss_config.client_key);

      const successUrl = `${window.location.origin}/bulk-purchase/success?orderId=${encodeURIComponent(order_id)}`;
      const failUrl = `${window.location.origin}/bulk-purchase/fail?orderId=${encodeURIComponent(order_id)}`;

      // 토스 카드 결제창 (NORMAL 플로우, 1회성)
      await tossPayments.requestPayment('카드', {
        amount,
        orderId: order_id,
        orderName: order_name,
        successUrl,
        failUrl,
        customerEmail: email.trim().toLowerCase(),
        customerName: contactName.trim(),
        customerMobilePhone: phone.replace(/[^0-9]/g, ''),
      });
      // requestPayment는 결제 성공 시 페이지가 successUrl로 이동하므로 이 아래는 보통 도달하지 않음
    } catch (err) {
      setIsProcessing(false);
      const msg = err?.message || '결제 요청 중 문제가 생겼어요.';
      // 토스 결제창 닫기 등은 에러로 따지지 않음
      if (err?.code === 'USER_CANCEL' || /USER_CANCEL/i.test(msg)) {
        trackSchoolPurchaseEvent('card_checkout_cancelled', checkoutMeta);
        setErrorMsg('결제를 취소했어요. 다시 시도해주세요.');
      } else {
        const reason = failReason === 'toss' && typeof err?.code === 'string' ? err.code : failReason;
        trackSchoolPurchaseEvent('card_checkout_failed', { ...checkoutMeta, reason });
        setErrorMsg(msg);
      }
    }
  };

  // 학교 예산 경로 — 같은 플랜·인원·promo로 견적서·구매 품의서·공문을 만들어 메일로 보낸다.
  // 품의는 학교 PC에서 올리므로 메일이 기본 전달 수단이고, 화면 다운로드는 보조다.
  const handleQuotation = async () => {
    if (quoteGenerating) return;
    setQuoteError('');
    if (!schoolName.trim()) {
      setQuoteError('학교(기관)명을 입력해주세요. 견적서와 구매 서류에 들어가는 이름이에요.');
      schoolInputRef.current?.focus();
      return;
    }
    const emailTrim = email.trim();
    if (!EMAIL_RE.test(emailTrim)) {
      setQuoteError('서류를 받을 이메일을 정확히 입력해주세요.');
      docsEmailInputRef.current?.focus();
      return;
    }
    const eventId = createQuoteEventId();
    const attribution = collectQuoteAttribution();

    setQuoteGenerating(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/user/generate-quotation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_name: schoolName.trim(),
          plan_id: planKey,
          quantity,
          contact_name: contactName.trim(),
          email: emailTrim,
          phone: phone.replace(/[^0-9]/g, '') || '',
          apply_promo: true,
          send_email: true,
          promo_code: activePromoCode || null,
          event_id: eventId,
          ...attribution,
          referrer: document.referrer || null,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || '서류를 만들지 못했어요.');
      }
      const data = await res.json();
      const packageDocs = [
        {
          type: 'quotation',
          label: '견적서',
          filename: data.filename,
          content: data.pdf_base64,
        },
        ...(data.supporting_documents || []),
      ];
      setQuoteDocs(packageDocs);
      setQuoteEmailSent(!!data.email_sent);
      setQuoteLockedUntil(data.price_locked ? formatKstDate(data.price_lock_expires_at) : '');
      trackSchoolPurchaseEvent('docs_requested', { entry: entrySource, path: 'docs', plan: planKey, quantity });

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_quotation', {
          school_name: schoolName || '(미입력)',
          plan: planKey,
          quantity,
          value: data.total_amount,
          currency: 'KRW',
        });
      }
      if (typeof window.fbq === 'function') {
        const pixelPayload = {
          content_name: '견적서',
          value: data.total_amount,
          currency: 'KRW',
        };
        window.fbq('track', 'Lead', pixelPayload, { eventID: eventId });
        window.fbq('trackCustom', 'QuoteRequested', pixelPayload, { eventID: eventId });
      }
      if (hasPro50Pricing) {
        trackPro50Event('quote_requested', {
          eventId,
          plan: planKey,
          stage: 'bulk_purchase',
        });
      } else if (isGroup50) {
        trackGroup50Event('quote_requested', {
          eventId,
          plan: planKey,
          stage: 'bulk_purchase',
        });
      }
      window.requestAnimationFrame?.(() => {
        docsDoneRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      });
    } catch (err) {
      trackSchoolPurchaseEvent('docs_request_failed', { entry: entrySource, path: 'docs', plan: planKey, quantity });
      setQuoteError(err.message || '지금 서류를 만들지 못했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setQuoteGenerating(false);
    }
  };

  const selectPath = (next) => {
    if (next === purchasePath) return;
    setPurchasePath(next);
    setErrorMsg('');
    setQuoteError('');
    trackSchoolPurchaseEvent('path_selected', { entry: entrySource, path: next, plan: planKey, quantity });
  };

  const officeNote = [
    '[왓퀴즈AI 구매 요청]',
    `학교: ${schoolName.trim() || '(학교명)'}`,
    `상품: 왓퀴즈AI PRO ${plan.label} ${quantity}명`,
    `금액: ${totalAmount.toLocaleString()}원 (부가세 포함)`,
    '구매 방법: S2B 학교장터 수의시담 또는 법인·공용카드',
    `학교장터 상품: ${S2B_PRODUCT_URL}`,
    `수취인(쿠폰 받을 선생님): ${contactName.trim() || '대표 선생님'} / ${email.trim()}`,
    '견적서·구매 품의서·공문은 메일로 받았어요.',
  ].join('\n');

  const handleCopyOfficeNote = async () => {
    try {
      await navigator.clipboard.writeText(officeNote);
      setOfficeNoteCopied(true);
      trackSchoolPurchaseEvent('office_note_copied', { entry: entrySource, path: 'docs', plan: planKey, quantity });
    } catch {
      setOfficeNoteCopied(false);
      setQuoteError('복사하지 못했어요. 메일로 받은 견적서를 그대로 전달해도 돼요.');
    }
  };

  if (isInApp()) {
    return (
      <AppPaymentBlocker
        pageName="대량 구매"
        webUrl={`https://whatquiz.co.kr${window.location.pathname}${window.location.search}`}
        fallbackPath="/pricing"
      />
    );
  }

  const isDocsPath = purchasePath === 'docs';
  const docsDone = isDocsPath && quoteDocs.length > 0;

  return (
    <div className={`bulk-page${docsDone ? '' : ' has-sticky-cta'}`}>
      <div className="bulk-container">

        <header className="bulk-header">
          <button className="bulk-back" onClick={() => navigate(-1)} aria-label="뒤로">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
          </button>
          <div>
            <h1 className="bulk-title">학교에서 구매하기</h1>
            <p className="bulk-subtitle">품의 서류를 받거나, 카드로 바로 결제할 수 있어요</p>
          </div>
        </header>

        {showPro50Banner && (
          <div className={`bulk-event${hasPro50Pricing ? '' : ' is-pending'}`} role="status">
            <b>
              {quoteLock && hasPro50Pricing
                ? `견적서 가격 적용 · ${formatKstDate(quoteLock.expiresAt)}까지`
                : `10월 이벤트 · 12개월 50%${pro50Active ? ` · ${PRO50_EVENT_END_LABEL}` : ''}`}
            </b>
            <span>
              {hasPro50Pricing && quoteLock && !pro50Active
                ? `견적서 ${quoteNumber}의 10월 이벤트가가 그대로 적용됐어요.`
                : hasPro50Pricing
                ? '인원 제한 없이 1명부터 이벤트가가 자동으로 적용됐어요.'
                : `지금 인원에서는 인원별 할인(${groupPrice.discountPercent}%)이 더 커서 그 가격으로 적용했어요.`}
            </span>
          </div>
        )}

        {/* group50 이벤트 진입에서만 노출 (9월) — 일반 진입에는 이벤트가를 보여주지 않는다 */}
        {isGroup50 && !showPro50Banner && (
          <div className={`bulk-event${hasGroup50Pricing ? '' : ' is-pending'}`} role="status">
            {/* 마감 표기는 날짜 경과 시 자동으로 빠진다 (가짜 긴급성 금지) */}
            <b>
              단체 이벤트 · {GROUP50_MIN_QUANTITY}명 이상 50%
              {isGroup50EventActive() ? ` · ${GROUP50_EVENT_END_LABEL}` : ''}
            </b>
            <span>
              {hasGroup50Pricing
                ? '이벤트가가 아래 단가에 자동으로 적용됐어요.'
                : `${GROUP50_MIN_QUANTITY}명 이상부터 50% 이벤트가가 적용돼요. 지금은 인원별 할인(${groupPrice.discountPercent}%) 가격이에요.`}
            </span>
          </div>
        )}

        <section className="bulk-section">
          <h2 className="bulk-section-title">어떻게 결제하세요?</h2>
          <div className="bulk-paths" role="radiogroup" aria-label="결제 방법">
            <button
              type="button"
              role="radio"
              aria-checked={isDocsPath}
              className={`bulk-path${isDocsPath ? ' active' : ''}`}
              onClick={() => selectPath('docs')}
            >
              <span className="bulk-path-title">
                학교 예산으로
                <em>대부분 이렇게 사요</em>
              </span>
              <span className="bulk-path-desc">품의용 견적서·품의서·공문을 메일로 받아요</span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!isDocsPath}
              className={`bulk-path${!isDocsPath ? ' active' : ''}`}
              onClick={() => selectPath('card')}
            >
              <span className="bulk-path-title">지금 카드로</span>
              <span className="bulk-path-desc">결제하면 쿠폰이 바로 메일로 와요</span>
            </button>
          </div>
        </section>

        <section className="bulk-section">
          <h2 className="bulk-section-title">구매 내용</h2>
          {pro50Active && (
            <div className="bulk-discounts" aria-label="10월 이벤트 할인율">
              {PRO50_PLAN_ORDER.map((key) => (
                <span key={key} className={key === planKey ? 'bulk-discount active' : 'bulk-discount'}>
                  {PRO50_PLAN_LABELS[key]} <strong>{getPro50DiscountPercent(key)}%</strong>
                </span>
              ))}
            </div>
          )}
          {!isGroup50 && !pro50Active && (
            <div className="bulk-discounts" aria-label={hasPromoPricing ? '전용 혜택' : '인원별 할인율'}>
              {hasPromoPricing ? (
                <span className="bulk-discount active">
                  전용 혜택 <strong>최대 50%</strong>
                </span>
              ) : (
                discountRows.map((row) => (
                  <span
                    key={row.label}
                    className={row.label === groupPrice.tierLabel ? 'bulk-discount active' : 'bulk-discount'}
                  >
                    {row.label} <strong>{row.discountPercent}%</strong>
                  </span>
                ))
              )}
            </div>
          )}

          <div className="bulk-field">
            <label htmlFor="bulk-school">학교(기관)명</label>
            <input
              id="bulk-school"
              ref={schoolInputRef}
              type="text"
              placeholder="예: 서울중학교"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              maxLength={80}
            />
          </div>

          <div className="bulk-field">
            <span className="bulk-field-label">기간</span>
            <div className="bulk-plans">
              {Object.entries(planLookup).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  className={`bulk-plan ${planKey === key ? 'active' : ''}`}
                  onClick={() => setPlanKey(key)}
                >
                  <span className="bulk-plan-label">{p.label}</span>
                  <span className="bulk-plan-price">
                    {priceFor(key, quantity).discountPercent > 0 && <del>{p.price.toLocaleString()}</del>}
                    <strong>{priceFor(key, quantity).unitPrice.toLocaleString()}원</strong>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="bulk-field">
            <span className="bulk-field-label">인원 (선생님 한 분당 쿠폰 1개)</span>
            <div className="bulk-stepper">
              <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>−</button>
              <input
                type="number"
                aria-label="인원"
                min={1}
                max={500}
                value={quantity}
                onChange={(e) => setQuantity(Math.min(500, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              />
              <button type="button" onClick={() => setQuantity(Math.min(500, quantity + 1))}>+</button>
              <span className="bulk-stepper-hint">최대 500명</span>
            </div>
          </div>

          <div className="bulk-total">
            <span>총 금액 (부가세 포함)</span>
            <strong>{totalAmount.toLocaleString()}원</strong>
          </div>
        </section>

        {isDocsPath && !docsDone && (
          <section className="bulk-section">
            <h2 className="bulk-section-title">서류 받을 곳</h2>
            <div className="bulk-field">
              <label htmlFor="bulk-docs-email">이메일</label>
              <input
                id="bulk-docs-email"
                ref={docsEmailInputRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="example@school.kr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={120}
              />
              <p className="bulk-field-hint">학교 PC에서 품의 올릴 때 바로 열 수 있게 메일로 보내드려요</p>
            </div>
            <div className="bulk-field">
              <label htmlFor="bulk-docs-name">
                이름 <span className="bulk-opt">선택</span>
              </label>
              <input
                id="bulk-docs-name"
                type="text"
                placeholder="예: 홍길동"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                maxLength={40}
              />
              <p className="bulk-field-hint">적어주시면 품의서와 학교장터 수취인에 넣어드려요</p>
            </div>
          </section>
        )}

        {docsDone && (
          <section ref={docsDoneRef} className="bulk-section bulk-done" role="status">
            <h2 className="bulk-done-title">
              {quoteEmailSent ? '서류를 메일로 보냈어요' : '서류가 준비됐어요'}
            </h2>
            <p className="bulk-done-desc">
              {quoteEmailSent
                ? `${email.trim()} 받은편지함을 확인해주세요`
                : '메일 발송이 늦어지고 있어요. 아래에서 바로 받아주세요'}
            </p>

            <h3 className="bulk-done-subtitle">이제 이렇게 하시면 돼요</h3>
            <ol className="bulk-steps">
              <li><b>받은 견적서·품의서로 품의 올리기</b></li>
              <li><b>행정실에 결제 요청하기</b><span>학교장터 수의시담이나 법인·공용카드로 결제해요</span></li>
              <li><b>선생님 메일로 쿠폰 도착</b><span>결제가 확인되면 인원수만큼 보내드려요</span></li>
              <li>
                <b>선생님들께 쿠폰과 사용 매뉴얼 전달</b>
                <span>
                  <a href="/docs/whatquiz-manual.pdf" target="_blank" rel="noopener noreferrer">사용 매뉴얼(PDF)</a>로
                  가입부터 쿠폰 등록, 기능별 사용법까지 화면을 보며 따라 할 수 있어요
                </span>
              </li>
            </ol>

            {quoteLockedUntil && (
              <p className="bulk-done-note">
                10월 이벤트가는 <strong>{quoteLockedUntil}까지</strong> 결제하시면 그대로 적용돼요.
              </p>
            )}

            <button type="button" className="bulk-submit" onClick={handleCopyOfficeNote}>
              {officeNoteCopied ? '복사했어요. 행정실 메신저에 붙여넣어 주세요' : '행정실에 보낼 안내문 복사'}
            </button>
            <a
              className="bulk-text-link"
              href={S2B_PRODUCT_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackSchoolPurchaseEvent('s2b_clicked', { entry: 'bulk_done', path: 'docs', plan: planKey, quantity })}
            >
              학교장터 상품 보기
            </a>

            {quoteError && <div className="bulk-error" role="alert">{quoteError}</div>}

            <details className="bulk-more">
              <summary>서류 파일 바로 받기</summary>
              <div className="bulk-quote-docs">
                {quoteDocs.map((doc) => (
                  <button
                    key={doc.filename}
                    type="button"
                    onClick={() => downloadBase64Pdf(doc.content, doc.filename)}
                  >
                    {doc.label || doc.type || 'PDF'}
                  </button>
                ))}
                {quoteDocs.length > 1 && (
                  <button type="button" onClick={() => downloadDocumentPackage(quoteDocs, schoolName)}>
                    전체 ZIP
                  </button>
                )}
              </div>
            </details>
          </section>
        )}

        {!isDocsPath && (
          <>
            <section className="bulk-section">
              <h2 className="bulk-section-title">결제자 정보</h2>
              <p className="bulk-section-desc">
                쿠폰 코드와 영수증을 아래 이메일과 휴대폰으로 보내드려요.
              </p>

              <div className="bulk-row">
                <div className="bulk-field">
                  <label htmlFor="bulk-card-name">담당자 이름</label>
                  <input id="bulk-card-name" type="text" placeholder="예: 홍길동" value={contactName} onChange={(e) => setContactName(e.target.value)} maxLength={40} />
                </div>
                <div className="bulk-field">
                  <label htmlFor="bulk-card-phone">휴대폰 번호</label>
                  <input id="bulk-card-phone" type="tel" inputMode="numeric" placeholder="01012345678" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} />
                </div>
              </div>

              <div className="bulk-field">
                <label htmlFor="bulk-card-email">이메일</label>
                <input id="bulk-card-email" type="email" inputMode="email" placeholder="example@school.kr" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} />
              </div>

              <details className="bulk-more">
                <summary>쿠폰 이름 정하기 (선택)</summary>
                <div className="bulk-field">
                  <input
                    type="text"
                    aria-label="쿠폰 이름"
                    placeholder="예: SEOUL12 (비워두면 자동으로 만들어요)"
                    value={couponPrefix}
                    onChange={(e) => setCouponPrefix(e.target.value.toUpperCase())}
                    maxLength={16}
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                  />
                  <p className="bulk-field-hint">
                    영문 대문자·숫자·하이픈 3~16자예요.{' '}
                    {couponPrefix ? (
                      <>
                        발급될 쿠폰: <code>{couponPrefix.toUpperCase()}-001</code> ~ <code>{couponPrefix.toUpperCase()}-{String(quantity).padStart(3, '0')}</code>
                      </>
                    ) : (
                      <>
                        예: <code>KARIM-001</code> ~ <code>KARIM-{String(quantity).padStart(3, '0')}</code>
                      </>
                    )}
                  </p>
                </div>
              </details>
            </section>

            <section className="bulk-section bulk-summary">
              <h2 className="bulk-section-title">결제 요약</h2>
              <table className="bulk-summary-table">
                <tbody>
                  <tr><td>상품</td><td>왓퀴즈AI PRO {plan.label}</td></tr>
                  <tr><td>단가</td><td>₩{unitPrice.toLocaleString()} <span className="bulk-summary-sub">({groupPrice.tierLabel}, {groupPrice.discountPercent}% 할인)</span></td></tr>
                  <tr><td>수량</td><td>{quantity}개</td></tr>
                  <tr><td>공급가액</td><td>₩{supply.toLocaleString()}</td></tr>
                  <tr><td>부가세 (10%)</td><td>₩{vat.toLocaleString()}</td></tr>
                  <tr className="bulk-summary-total"><td>총 결제 금액</td><td>₩{totalAmount.toLocaleString()}</td></tr>
                </tbody>
              </table>

              <div className="bulk-notice">
                <p className="bulk-notice-title">결제 전 확인해주세요</p>
                <ul>
                  <li><strong>쿠폰 {quantity}개</strong>가 나와요. 결제한 분 계정에 PRO가 자동으로 켜지지는 않아요.</li>
                  <li>쿠폰은 결제 즉시 이메일·휴대폰으로 가고, 선생님 한 분당 1개씩 등록하면 돼요.</li>
                  <li>토스페이먼츠 카드매출전표가 메일에 함께 가요 (회계 증빙용).</li>
                  <li>환불은 <a href="mailto:angel@plactical.com">angel@plactical.com</a>으로 문의해주시면 영업일 1~2일 안에 안내드려요. 이미 등록한 쿠폰은 환불되지 않아요.</li>
                  <li>세금계산서가 필요하면 결제 후 회신해주세요.</li>
                </ul>
              </div>

              <label className="bulk-agree">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                <span>위 내용을 확인했고 카드 결제에 동의해요.</span>
              </label>

              <p className="bulk-footnote">
                결제는 토스페이먼츠 안전결제로 처리돼요.
              </p>
            </section>
          </>
        )}

        {!docsDone && (
          <div className="bulk-cta-bar">
            {isDocsPath && quoteError && <div className="bulk-error" role="alert">{quoteError}</div>}
            {!isDocsPath && errorMsg && <div className="bulk-error" role="alert">{errorMsg}</div>}
            {isDocsPath ? (
              <button
                type="button"
                className="bulk-submit"
                onClick={handleQuotation}
                disabled={quoteGenerating}
                aria-busy={quoteGenerating || undefined}
              >
                {quoteGenerating ? '서류 만드는 중…' : '서류 메일로 받기'}
              </button>
            ) : (
              <button
                type="button"
                className="bulk-submit"
                onClick={handleSubmit}
                disabled={isProcessing || !tossReady}
                aria-busy={isProcessing || undefined}
              >
                {isProcessing
                  ? '결제창 여는 중…'
                  : `₩${totalAmount.toLocaleString()} 카드로 결제하기`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const BulkPurchaseRoute = () => {
  const [searchParams] = useSearchParams();
  const linkToken = (searchParams.get('link') || '').trim();
  return linkToken ? <BulkPaymentLinkCheckout token={linkToken} /> : <BulkPurchasePage />;
};

export default BulkPurchaseRoute;
